import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";

import { SelectionListItem } from "../../models/selection-list-item.model";
import { BaseControlValueAccessor, MakeValueAccessorProvider } from "../base-control-value-accessor.component";

export interface ComboboxItem {
    extraLabelText: string;
    isDisabled: boolean;
    text: string;
    value: string | number | boolean;
}

@Component({
    selector: "ha-combobox",
    templateUrl: "./combobox.component.html",
    styleUrls: ["./combobox.component.scss"],
    providers: [MakeValueAccessorProvider(ComboboxComponent)],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboboxComponent extends BaseControlValueAccessor<string> implements AfterViewInit, OnDestroy {
    @Input()
    public label: string;

    @Input()
    public set options(value: SelectionListItem[] | null) {
        this.allItems = this.generateListItems(value);
        this.updateSelectedItemFromValue();
        this.filterItems();
    }

    @Input()
    public sortWhenTranslated: boolean;

    @Input()
    public name: string;

    @ViewChild("inputElement", { static: false })
    private inputElement: ElementRef<HTMLInputElement>;

    public allItems: ComboboxItem[] = [];
    public filteredItems: ComboboxItem[] = [];
    public searchText: string = "";
    public expanded = false;
    public selectedItem: ComboboxItem | undefined;

    private documentClickHandler: ((event: MouseEvent) => void) | null = null;

    public override get value(): string {
        return super.value;
    }

    public override set value(v: string) {
        super.value = v;
        this.updateSelectedItemFromValue();
    }

    public get hasNeitherValueNorFocusNorPrefix(): boolean {
        return !this.searchText && !this.hasFocus;
    }

    public ngAfterViewInit(): void {
        // Add document click listener to close dropdown when clicking outside
        this.documentClickHandler = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const controlElement = this.inputElement?.nativeElement.closest(".control");

            if (controlElement && !controlElement.contains(target) && this.expanded) {
                this.close();
            }
        };

        // Use setTimeout to avoid immediate execution during component initialization
        setTimeout(() => {
            document.addEventListener("click", this.documentClickHandler!);
        }, 0);
    }

    public ngOnDestroy(): void {
        if (this.documentClickHandler) {
            document.removeEventListener("click", this.documentClickHandler);
            this.documentClickHandler = null;
        }
    }

    public generateListItems(options: SelectionListItem[] | null): ComboboxItem[] {
        const translocoService = this.injector.get(TranslocoService);

        const listItems = options?.filter((x) => !x.isDisabled).map((item) => {
            return <ComboboxItem> {
                extraLabelText: item.extraLabelLangKey != null
                    ? translocoService.translate(item.extraLabelLangKey)
                    : item.extraLabelText,
                isDisabled: item.isDisabled,
                text: item.langKey != null ? translocoService.translate(item.langKey) : item.text,
                value: item.value,
            };
        });

        return this.sortWhenTranslated
            ? listItems?.sort((a, b) => {
                // sort items with undefined or null values on top as we assume this is the default.
                if (!b.value) {
                    return 1;
                }

                return (a.text < b.text ? -1 : 1);
            })
            : listItems;
    }

    public filterItems(): void {
        if (!this.searchText || this.searchText.trim() === "") {
            this.filteredItems = this.allItems;
        } else {
            const searchLower = this.searchText.toLowerCase();
            this.filteredItems = this.allItems.filter((item) =>
                item.text.toLowerCase().includes(searchLower)
                || (item.extraLabelText && item.extraLabelText.toLowerCase().includes(searchLower))
            );
        }
        this.changeDetectorRef.detectChanges();
    }

    public onInputChange(value: string): void {
        this.searchText = value;
        this.expanded = true;
        this.filterItems();

        // Clear selection if search text doesn't match selected item
        if (this.selectedItem && this.selectedItem.text !== value) {
            this.selectedItem = undefined;
            this.value = undefined;
        }

        this.changeDetectorRef.detectChanges();
    }

    private updateSelectedItemFromValue(): void {
        if (this.value && this.allItems.length > 0) {
            const item = this.allItems.find((i) => i.value === this.value);
            if (item) {
                this.selectedItem = item;
                this.searchText = item.text;
            } else {
                this.selectedItem = undefined;
            }
        } else {
            this.selectedItem = undefined;
            if (!this.hasFocus) {
                this.searchText = "";
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    public onBoxClick(): void {
        if (!this.isDisabled) {
            this.expanded = true;
            this.filterItems();
            this.inputElement?.nativeElement.focus();
            this.changeDetectorRef.detectChanges();
        }
    }

    public onInputFocus(): void {
        this.onFocus();
        this.expanded = true;
        this.filterItems();
    }

    public onInputBlur(event: FocusEvent): void {
        // Delay blur to allow click on dropdown item
        setTimeout(() => {
            const activeElement = document.activeElement;
            const controlElement = this.inputElement?.nativeElement.closest(".control");
            const isClickingWithinControl = activeElement && controlElement?.contains(activeElement);

            if (!isClickingWithinControl) {
                this.onBlur(event);
                this.expanded = false;
                this.changeDetectorRef.detectChanges();
            }
        }, 200);
    }

    public selectOption(option: ComboboxItem): void {
        if (option.isDisabled) {
            return;
        }

        this.selectedItem = option;
        this.searchText = option.text;
        this.value = option.value as string;
        this.expanded = false;
        this.inputElement?.nativeElement.focus();
        this.changeDetectorRef.detectChanges();
    }

    public close(): void {
        this.expanded = false;
        this.changeDetectorRef.detectChanges();
    }

    public toggleExpand(): void {
        this.expanded = !this.expanded;
        if (this.expanded) {
            this.filterItems();
        }
        this.changeDetectorRef.detectChanges();
    }

    public override writeValue(value: string): void {
        super.writeValue(value);
        this.updateSelectedItemFromValue();
    }
}
